import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../assets/images/cup-artie.png';
import { Card } from "@/components/ui/card";
import { Loader2, Check } from "lucide-react";
import { useOnboarding } from "@/context/OnboardingContext";
import { useArtieScore } from "@/hooks/use-artie-score";
import { useInventoryMode } from "@/hooks/useInventoryMode";
import { toast } from "sonner";

export default function NewProjectLoading() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { data } = useOnboarding();

    // Get project ID from context OR URL query param
    const projectId = data.projectId || searchParams.get('projectId');

    // In inventory mode nothing computes a score, and this screen only leaves when scoreData
    // arrives — so it would fill its progress bar and then wait forever. Leave before that happens
    // rather than making a creator watch a countdown for work that is not running.
    //
    // null means the answer has not arrived yet and must NOT be read as false, or the page would
    // start waiting for a beat and then correct itself in front of the creator.
    const inventoryMode = useInventoryMode();

    // Pass true to suppress the generic "Score Recalculated" toast
    const { scoreData, error, refresh } = useArtieScore(projectId, true);
    const [currentStep, setCurrentStep] = useState(1);
    const [progressPercent, setProgressPercent] = useState(0);

    // Total steps
    const TOTAL_STEPS = 4;
    // Time per step in ms (increased duration)
    const STEP_DURATION = 15000;

    useEffect(() => {
        if (inventoryMode) {
            navigate(
                projectId ? `/upload-thanks?projectId=${projectId}` : '/upload-thanks',
                { replace: true },
            );
        }
    }, [inventoryMode, projectId, navigate]);

    useEffect(() => {
        // Skipped in inventory mode: refresh() asks for a score that is not being calculated, and
        // the redirect above is already on its way.
        if (inventoryMode !== false) return;
        if (!projectId) {
            // Only redirect if we truly have no ID from either source
            toast.error("Project ID missing. Redirecting...");
            navigate('/dashboard');
            return;
        }
        // Just check status on mount, don't force recalculation
        refresh();
    }, [inventoryMode, projectId, refresh, navigate]);

    useEffect(() => {
        let stepInterval: NodeJS.Timeout;
        let progressInterval: NodeJS.Timeout;

        // Function to handle normal progression
        const runProgress = () => {
            // Update steps according to duration
            stepInterval = setInterval(() => {
                setCurrentStep((prev) => {
                    // If we have score data, we can proceed to completion
                    if (scoreData) {
                        return Math.min(prev + 1, TOTAL_STEPS + 1); // +1 to trigger completion
                    }
                    // Otherwise, stop at the last step (wait for scoreData)
                    return Math.min(prev + 1, TOTAL_STEPS);
                });
            }, STEP_DURATION);

            // Smooth progress bar animation
            const increment = 25 / (STEP_DURATION / 50);

            progressInterval = setInterval(() => {
                setProgressPercent((prev) => {
                    const targetPercent = Math.min(currentStep * 25, 100);

                    // If calculation is done, we accelerate to 100%
                    if (scoreData) {
                        const remaining = 100 - prev;
                        // Accelerate: move 10% of remaining distance or at least 5% per tick
                        return Math.min(prev + Math.max(remaining * 0.2, 5), 100);
                    }

                    // If we are at the last step and waiting, stall at 90-95%
                    if (currentStep === TOTAL_STEPS && !scoreData) {
                        return Math.min(prev + 0.1, 95);
                    }

                    // Normal progression
                    if (prev < targetPercent) {
                        return prev + increment;
                    }
                    return prev;
                });
            }, 50); // Update every 50ms for smooth animation
        };

        runProgress();

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, [currentStep, scoreData]);

    // Handle completion navigation
    useEffect(() => {
        // If we have score data, navigate directly to initial-assessment
        // Use replace: true to remove the loading page from history
        if (scoreData) {
            navigate(`/initial-assessment?projectId=${projectId}`, { replace: true });
        }
    }, [scoreData, navigate, projectId]);

    // Handle error state
    useEffect(() => {
        // INVENTORY MODE IS NOT AN ERROR — same trap as the signup wizard's loader. use-artie-score
        // reports "scoring is turned off" through `error` so the screens reading it stop waiting for a
        // score that is never coming, and this handler read that as a failed analysis: a red toast on a
        // SUCCESSFUL upload, then back to step 3, whose own guard bounces to step 1 once the wizard
        // context has been cleared. The creator ends up at the start of the wizard they just completed.
        //
        // The effect above already sends them to the thank-you page in this mode.
        if (inventoryMode) return;

        if (error) {
            console.error("Error in loading:", error);
            toast.error("Something went wrong with the analysis. Please try again.", {
                style: {
                    background: '#ef4444',
                    color: 'white',
                    border: 'none'
                }
            });
            // Go back to review page on error so they can retry
            navigate('/projects/create/step-3');
        }
    }, [error, inventoryMode, navigate]);

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden overflow-y-auto font-sans">

            <main className="relative z-10 w-full flex flex-col items-center justify-start py-12 px-4">

                {/* Logo */}
                <div className="mb-8 relative flex justify-center">
                    <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                    <img
                        src={logo}
                        alt="Artie"
                        className="w-48 h-48 object-contain relative z-10 max-w-full"
                    />
                </div>

                <div className="flex flex-col items-center max-w-2xl w-full animate-in slide-in-from-bottom-4 duration-700">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center text-[oklch(0.2686_0_0)] font-cabinet tracking-tight leading-tight">
                        Giving your script the <br /> sniff test
                    </h1>
                    <p className="text-lg text-[oklch(0.55_0_0)] text-center mb-10 max-w-xl font-ibm leading-relaxed">
                        Hang tight. It’ll be a few minutes. I’m giving your script an early read to spot brand-friendly moments, audience signals, and overall potential. Promise I won’t eat it.
                    </p>

                    <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden p-8 rounded-[12px] relative">
                        {/* Progress Bar Top */}
                        <div
                            className="absolute top-0 left-0 h-1 bg-black transition-all duration-500 ease-in-out"
                            style={{ width: `${progressPercent}%` }}
                        ></div>

                        <div className="space-y-8">
                            <StepItem
                                stepNumber={1}
                                currentStep={currentStep}
                                title="Warming up"
                                description="Getting everything ready"
                            />
                            <StepItem
                                stepNumber={2}
                                currentStep={currentStep}
                                title="Reading your script"
                                description="Looking for moments that feel natural and story-true"
                            />
                            <StepItem
                                stepNumber={3}
                                currentStep={currentStep}
                                title="Exploring brand fit"
                                description="Noticing where brands could belong without breaking the story"
                            />
                            <StepItem
                                stepNumber={4}
                                currentStep={currentStep}
                                title="Pulling together early insights"
                                description="Highlights you’ll see right away"
                            />
                        </div>
                    </Card>
                </div>
            </main>
        </div>
    );
}

function StepItem({ stepNumber, currentStep, title, description }: { stepNumber: number, currentStep: number, title: string, description: string }) {
    const isCompleted = currentStep > stepNumber;
    const isActive = currentStep === stepNumber;
    const isPending = currentStep < stepNumber;

    return (
        <div className={`flex gap-4 ${isPending ? 'opacity-40' : 'opacity-100'} transition-opacity duration-300`}>
            <div className="shrink-0">
                {isActive ? (
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : isCompleted ? (
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white">
                        <Check className="w-5 h-5" />
                    </div>
                ) : (
                    <div className="w-8 h-8 rounded-full border border-[oklch(0.85_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] font-medium text-sm">
                        {stepNumber}
                    </div>
                )}
            </div>
            <div>
                <h3 className={`text-base ${isActive || isCompleted ? 'font-bold text-[oklch(0.2686_0_0)]' : 'font-medium text-[oklch(0.2686_0_0)]'}`}>
                    {title}
                </h3>
                <p className="text-[oklch(0.55_0_0)] text-sm mt-0.5">{description}</p>
            </div>
        </div>
    );
}